import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeatureAccess, OFFICER_FEATURES } from '../../utils/rolePermissions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DemoAccountStatus from './components/DemoAccountStatus';
import AuthTableMonitor from './components/AuthTableMonitor';
import EndpointTester from './components/EndpointTester';
import SystemHealthIndicator from './components/SystemHealthIndicator';
import LogConsole from './components/LogConsole';


const DebugDashboard = () => {
  const { user, userProfile } = useAuth();
  const [logs, setLogs] = useState([]);
  const [systemHealth, setSystemHealth] = useState({
    supabaseConnected: false,
    apiResponseTime: null,
    lastChecked: null
  });

  // Check officer access
  const hasAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.DEBUG_DASHBOARD);

  // Add log entry
  const addLog = (level, message, data = {}) => {
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp: new Date()?.toISOString(),
      level,
      message,
      data
    };
    setLogs(prev => [logEntry, ...prev]?.slice(0, 100)); // Keep last 100 logs
  };

  // Check system health
  const checkSystemHealth = async () => {
    const startTime = Date.now();
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('count')?.limit(1);
      const responseTime = Date.now() - startTime;
      
      setSystemHealth({
        supabaseConnected: !error,
        apiResponseTime: responseTime,
        lastChecked: new Date()?.toISOString()
      });
      
      addLog('info', 'System health check completed', { responseTime, success: !error });
    } catch (error) {
      setSystemHealth({
        supabaseConnected: false,
        apiResponseTime: null,
        lastChecked: new Date()?.toISOString()
      });
      addLog('error', 'System health check failed', { error: error?.message });
    }
  };

  // Initial health check
  useEffect(() => {
    checkSystemHealth();
    const interval = setInterval(checkSystemHealth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Clear logs
  const clearLogs = () => {
    setLogs([]);
    addLog('info', 'Logs cleared');
  };

  // Export logs
  const exportLogs = () => {
    const logsJson = JSON.stringify(logs, null, 2);
    const blob = new Blob([logsJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-logs-${new Date()?.toISOString()}.json`;
    a?.click();
    URL.revokeObjectURL(url);
    addLog('info', 'Logs exported');
  };

  return (
    <>
      <Helmet>
        <title>Debug Dashboard - MyBodyToday</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-background p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <Icon name="Bug" size={24} className="text-red-500" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Debug Dashboard</h1>
                  <p className="text-sm text-muted-foreground">Authentication Monitoring & Troubleshooting</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={checkSystemHealth}
                  className="flex items-center gap-2"
                >
                  <Icon name="RefreshCw" size={16} />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* System Health Indicator */}
          <SystemHealthIndicator health={systemHealth} onRefresh={checkSystemHealth} />

          {/* Main Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Demo Account Status */}
            <DemoAccountStatus addLog={addLog} />
            
            {/* Auth Table Monitor */}
            <AuthTableMonitor addLog={addLog} />
          </div>

          {/* Endpoint Tester - Full Width */}
          <div className="mb-6">
            <EndpointTester addLog={addLog} />
          </div>

          {/* Log Console - Full Width */}
          <LogConsole 
            logs={logs} 
            onClear={clearLogs} 
            onExport={exportLogs}
          />
        </div>
      </div>
    </>
  );
};

export default DebugDashboard;